import React, { memo, useMemo } from 'react';
import { CategoryLandingPageSectionBlokProps } from './CategoryLandingPage';
import Heading from '../Heading/Heading';
import { HeadingTypes } from '../Heading/Heading';
import { CTA } from '../../interfaces/CTA.interface';
import StoryblokLinkProvider from '../Utils/StoryblokLinkProvider/StoryblokLinkProvider';
import ButtonLink from '../ButtonLink/ButtonLink';
import { ButtonType } from '../../interfaces/enums/ButtonType.enum';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import RichText, { screenWidth } from '../RichText/RichText';

interface CategoryLandingPageSectionHeaderProps {
  categoryLandingPageSections: CategoryLandingPageSectionBlokProps[];
  selectedCategoryLandingPageSection: CategoryLandingPageSectionBlokProps;
}

const CategoryLandingPageSectionHeader: React.FC<
  CategoryLandingPageSectionHeaderProps
> = ({ categoryLandingPageSections, selectedCategoryLandingPageSection }) => {
  const { heading, body, cta } = selectedCategoryLandingPageSection;

  const indexOfSelectedCategoryLandingPageSection: number | undefined =
    useMemo(() => {
      const index: number = categoryLandingPageSections?.findIndex(
        (categoryLandingPageSection: CategoryLandingPageSectionBlokProps) =>
          categoryLandingPageSection?._uid ===
          selectedCategoryLandingPageSection?._uid
      );
      if (index > -1) return index + 1;
      return undefined;
    }, [categoryLandingPageSections, selectedCategoryLandingPageSection]);

  return (
    <div className="flex flex-col items-center justify-center gap-y-6 xl:flex-row xl:items-start xl:justify-between xl:gap-y-0 xl:gap-x-6">
      <div className="w-full flex items-center justify-center gap-x-6 xl:items-start xl:justify-start">
        <div className="hidden w-12 h-12 rounded-full items-center justify-center bg-text/body-accent text-system/white xl:flex xl:flex-none">
          <p className="font-dm-serif-display text-2xl">
            {indexOfSelectedCategoryLandingPageSection}
          </p>
        </div>
        <div className="space-y-2 text-center xl:text-left">
          {selectedCategoryLandingPageSection?.heading && (
            <Heading
              text={selectedCategoryLandingPageSection.heading}
              headingType={HeadingTypes.h2}
            />
          )}
          <RichText
            content={selectedCategoryLandingPageSection.body}
            centerResponsively={screenWidth.XL}
          />
        </div>
      </div>
      {selectedCategoryLandingPageSection.cta?.map((cta: CTA) => (
        <StoryblokLinkProvider
          key={cta._uid}
          link={cta.link}
          render={(link) => (
            <ButtonLink
              url={link}
              label={cta.label}
              type={ButtonType.SECONDARY}
              size={ButtonSize.XL}
              className="flex-shrink-0"
            >
              {cta.label}
            </ButtonLink>
          )}
        />
      ))}
    </div>
  );
};

export default memo(CategoryLandingPageSectionHeader);
